import React, { useEffect, useState } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { List, Typography, Button, Card, message } from 'antd'
import { VideoCameraOutlined, PlayCircleOutlined } from '@ant-design/icons'
import * as liveSessionsService from '../../services/liveSessions'
import useCoursePrivilege from '../../hooks/useCourseprivilege'

const { Title, Text } = Typography

const formatCountdown = (scheduledAt) => {
  const now = new Date()
  const then = new Date(scheduledAt)
  const diff = then - now
  if (diff <= 0) return null
  const days = Math.floor(diff / (1000 * 60 * 60 * 24))
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
  const parts = []
  if (days > 0) parts.push(`${days}d`)
  if (hours > 0) parts.push(`${hours}h`)
  parts.push(`${minutes}m`)
  return parts.join(' ')
}

const LiveSessionsList = () => {
  const { courseId } = useParams()
  const history = useHistory()
  const { privilege } = useCoursePrivilege(courseId)
  const [sessions, setSessions] = useState([])
  const [loading, setLoading] = useState(true)
  const [countdown, setCountdown] = useState({})

  useEffect(() => {
    const load = async () => {
      try {
        const list = await liveSessionsService.listByCourse(courseId)
        setSessions(list || [])
      } catch (e) {
        message.error(e?.response?.data?.message || 'Failed to load sessions')
      } finally {
        setLoading(false)
      }
    }
    if (courseId) load()
  }, [courseId])

  useEffect(() => {
    const interval = setInterval(() => {
      const next = {}
      sessions.forEach((s) => {
        if (s.status === 'scheduled') {
          const cd = formatCountdown(s.scheduledAt)
          if (cd) next[s.id || s._id] = cd
        }
      })
      setCountdown(next)
    }, 1000)
    return () => clearInterval(interval)
  }, [sessions])

  const upcoming = sessions.filter(
    (s) => s.status === 'scheduled' || s.status === 'live'
  )

  return (
    <div style={{ padding: '0 8px' }}>
      <Title level={3} style={{ marginBottom: 16 }}>
        Live Sessions
      </Title>
      {privilege !== 'student' && (
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Schedule and start sessions from Course Settings.
        </Text>
      )}
      <List
        loading={loading}
        dataSource={upcoming}
        locale={{ emptyText: 'No upcoming live sessions.' }}
        renderItem={(session) => {
          const sid = session.id || session._id
          const scheduledAt = new Date(session.scheduledAt)
          const isLive = session.status === 'live'
          const joinableFrom = new Date(scheduledAt.getTime() - 30 * 60 * 1000)
          const canJoin = isLive || Date.now() >= joinableFrom.getTime()
          const cd = countdown[sid]

          return (
            <Card size="small" style={{ marginBottom: 12 }}>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 12,
                  justifyContent: 'space-between'
                }}
              >
                <div>
                  <Text strong>
                    {session.title}
                    {isLive && (
                      <Text type="danger" style={{ marginLeft: 8 }}>
                        · Live
                      </Text>
                    )}
                  </Text>
                  <br />
                  <Text type="secondary">
                    {scheduledAt.toLocaleString()} · {session.duration} min
                    {session.description && ` · ${session.description}`}
                  </Text>
                  {!isLive && cd && (
                    <div style={{ marginTop: 4 }}>
                      <Text type="secondary">Starts in: {cd}</Text>
                    </div>
                  )}
                </div>
                <div>
                  {canJoin && (
                    <Button
                      type="primary"
                      icon={<PlayCircleOutlined />}
                      onClick={() =>
                        window.open(
                          `${window.location.origin}/app/course/${courseId}/live/${sid}`,
                          '_blank',
                          'noopener,noreferrer'
                        )
                      }
                    >
                      Join Session
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          )
        }}
      />
    </div>
  )
}

export default LiveSessionsList
